'use client'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'

import { fetchToolbarLinks, TOOLBAR_PATH_MAP } from '@/src/constants/bottomToolbar'
import { getActiveToolbarTab } from '@/src/utils/navigation/getActiveToolbarTab'

import { BottomToolbar } from './bottomToolbar'
import isAeDomain from '@/src/utils/generalUtils/isAeDomain'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'

interface BottomToolbarWrapperProps {
    showVisa?: boolean
    showFlightsHotels?: boolean
}

const BottomToolbarWrapper: React.FC<BottomToolbarWrapperProps> = ({
    showVisa,
    showFlightsHotels
}) => {
    const router = useRouter()
    const pathname = usePathname()
    const t = useTranslations('common')
    const locale = useLocale()

    // Initialize with active tab based on current URL
    const [currentValue, setCurrentValue] = useState<string>(() => getActiveToolbarTab(pathname))

    // Update active tab when pathname changes
    useEffect(() => {
        const activeTab = getActiveToolbarTab(pathname)
        setCurrentValue(activeTab)
    }, [pathname])

    const handleNavigationChange = useCallback(
        (value: string) => {
            const basePath = TOOLBAR_PATH_MAP[value];
            const noLocaleRoutes = ['hotel','visa']
            if (!basePath) return;

            const path = noLocaleRoutes.includes(value) ? basePath : `/${locale}${basePath}`;

            const externalRoutes : Record<string, string> = {
                hotel: isCleartrip()
                    ? '/hotels'
                    : isAeDomain()
                        ? `https://www.flyin.com/${locale}/hotels`
                        : `${window.location.origin}/${locale}/hotels`,
                fph: `https://www.flyin.com/${locale}/flights-hotels`
            };

            if (externalRoutes[value]) {
                window.location.href = externalRoutes[value];
            } else {
                router.push(path);
            }
        },
        [router, locale]
    );
    const toolbarLinks = useMemo(
        () => fetchToolbarLinks({ showVisa, showFlightsHotels }),
        [showVisa, showFlightsHotels]
    )
    // Translate toolbar links
    const translatedToolbarLinks = useMemo(
        () =>
            toolbarLinks.map(link => ({
                ...link,
                label: t(link.label)
            })),
        [t, locale]
    )

    return (
        <BottomToolbar
            currentValue={currentValue}
            toolbarLinks={translatedToolbarLinks}
            onNavigationChange={handleNavigationChange}
        />
    )
}

export { BottomToolbarWrapper }
export default BottomToolbarWrapper
